// locationService — resuelve la región del lector.
//
// Estrategia (en orden):
//   1. Selección MANUAL (el usuario eligió una región del selector):
//      gana sobre todo, no se sobrescribe.
//   2. Coordenadas del navegador (navigator.geolocation) → endpoint de
//      reverse geocoding del backend, que resuelve contra Ecolab.
//   3. Si el usuario rechaza geolocation → sin ubicación → global.
//   4. Sin ubicación → null → el backend cae a la edición global.
//
// Cache localStorage: 24h para detección automática, sin expiración
// para selección manual.

const KEY_MANUAL       = 'newsRegionManual';         // { municipioId, departmentId, name }
const KEY_AUTO         = 'newsRegionAuto';           // { municipioId, departmentId, name, source, at }
const AUTO_TTL_MS      = 24 * 60 * 60 * 1000;        // 24 h
const GEO_TIMEOUT_MS   = 8000;

const API_BASE_URL =
  (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/v1';

// ── Helpers de storage ────────────────────────────────────────────────
const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeJson = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
};

// ── Detección por coordenadas → reverse geocoding vía backend ─────────
const detectByCoordinates = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  const timeout = setTimeout(() => resolve(null), GEO_TIMEOUT_MS);
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      clearTimeout(timeout);
      try {
        const { latitude, longitude } = pos.coords;
        // El backend proxifica a Ecolab /municipios/mas-cercano.
        const resp = await fetch(
          `${API_BASE_URL}/geografia/municipios/mas-cercano?lat=${latitude}&lng=${longitude}&paisId=1045`);
        if (resp.status === 204) return resolve(null);
        if (!resp.ok) return resolve(null);
        const data = await resp.json();
        resolve({
          municipioId:    data.municipioId,
          departmentId:   data.departamentoId,
          municipioName:  data.nombre,
          departmentName: data.departamentoNombre,
        });
      } catch { resolve(null); }
    },
    () => { clearTimeout(timeout); resolve(null); },
    { timeout: GEO_TIMEOUT_MS, maximumAge: 60 * 60 * 1000 }
  );
});

// ── API pública ───────────────────────────────────────────────────────
export const locationService = {
  /**
   * Devuelve la región del lector como { municipioId, departmentId, name }.
   * Cachea en localStorage por 24 h.
   */
  async getRegion() {
    const manual = readJson(KEY_MANUAL);
    if (manual?.departmentId != null) return manual;

    const auto = readJson(KEY_AUTO);
    if (auto?.departmentId != null && (Date.now() - (auto.at ?? 0)) < AUTO_TTL_MS) {
      return auto;
    }

    const detected = await detectByCoordinates();
    if (detected) {
      const record = { ...detected, source: 'geolocation', at: Date.now() };
      writeJson(KEY_AUTO, record);
      return record;
    }
    return null;
  },

  setManualRegion({ municipioId, departmentId, name }) {
    writeJson(KEY_MANUAL, { municipioId, departmentId, name });
  },

  clearManualRegion() {
    try { localStorage.removeItem(KEY_MANUAL); } catch {}
  },

  getRegionInfo() {
    const manual = readJson(KEY_MANUAL);
    if (manual?.departmentId != null) return { ...manual, isManual: true };
    const auto = readJson(KEY_AUTO);
    if (auto?.departmentId != null) return { ...auto, isManual: false };
    return { departmentId: null };
  },
};
